import React, { useState, useEffect } from 'react';
import { AppIcon } from '../../icons/appIcons.tsx';
import EmptyState from '../common/EmptyState.tsx';

interface Photo {
  id: string;
  fileName: string;
  originalFileName: string;
  displayOrder: number;
}

interface Post {
  id: string;
  title: string;
  content: string;
  createdAt: string;
  creator: {
    firstName: string;
    lastName: string;
  };
  photos: Photo[];
}

interface GirlsCornerArchiveProps {
  onClose: () => void;
}

export default function GirlsCornerArchive({ onClose }: GirlsCornerArchiveProps) {
  const [posts, setPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedPostId, setExpandedPostId] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    fetchArchive();
  }, [page]);

  const fetchArchive = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('authToken');
      const response = await fetch(`/api/girlscorner/archive?page=${page}&limit=10`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();
      if (data.success) {
        setPosts(data.data);
        setTotalPages(data.pagination.totalPages);
      }
    } catch (error) {
      console.error('Error fetching archive:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleExpand = (postId: string) => {
    setExpandedPostId(expandedPostId === postId ? null : postId);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  };

  return (
    <div className="px-4 sm:px-6 lg:px-12 py-4 sm:py-6 lg:py-10">
      {/* Header */}
      <div className="mb-6 sm:mb-8 flex items-center justify-between">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 flex items-center gap-3">
          <AppIcon name="directory" className="w-8 h-8 sm:w-9 sm:h-9 text-ggaa-green shrink-0" />
          <span>Girls' Corner Archive</span>
        </h1>
        <button
          onClick={onClose}
          className="px-4 py-2 text-ggaa-green hover:text-ggaa-dark font-medium text-sm sm:text-base"
        >
          ← Back to Current
        </button>
      </div>

      {/* Posts List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-ggaa-green"></div>
          <span className="ml-4 text-gray-600">Loading archive...</span>
        </div>
      ) : posts.length === 0 ? (
        <div className="bg-white rounded-xl shadow-lg p-12 text-center">
          <EmptyState icon="emptyInbox" title="No Archived Posts" description="Past Girls' Corner posts will appear here" />
        </div>
      ) : (
        <div>
          <div className="space-y-4">
            {posts.map((post) => (
              <div
                key={post.id}
                className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden"
              >
                {/* Post Header */}
                <div 
                  className="p-4 sm:p-6 cursor-pointer hover:bg-gray-50 transition-colors"
                  onClick={() => toggleExpand(post.id)}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="text-sm text-gray-500 mb-1">{formatDate(post.createdAt)}</div>
                      <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2">{post.title}</h3>
                      {expandedPostId !== post.id && (
                        <p className="text-sm text-gray-600 line-clamp-2">
                          {post.content}
                        </p>
                      )}
                      {post.photos.length > 0 && (
                        <div className="mt-2 text-xs text-gray-500">
                          <span className="inline-flex items-center gap-1">
                            <AppIcon name="camera" className="w-3.5 h-3.5" />
                            {post.photos.length} photo{post.photos.length !== 1 ? 's' : ''}
                          </span>
                        </div>
                      )}
                    </div>
                    <button className="ml-4 text-gray-400 hover:text-gray-600 flex-shrink-0">
                      {expandedPostId === post.id ? '▲' : '▼'}
                    </button>
                  </div>
                </div>

                {/* Expanded Content */}
                {expandedPostId === post.id && (
                  <div className="px-4 sm:px-6 pb-4 sm:pb-6 border-t border-gray-100">
                    <div className="prose max-w-none mt-4">
                      <div className="text-gray-700 whitespace-pre-wrap text-sm sm:text-base leading-relaxed">
                        {post.content}
                      </div>
                    </div>

                    {/* Photos */}
                    {post.photos.length > 0 && (
                      <div className="mt-6">
                        <h4 className="text-sm font-semibold text-gray-700 mb-3">Photos:</h4>
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                          {post.photos.map((photo) => (
                            <div key={photo.id} className="group relative">
                              <img
                                src={`/uploads/girlscorner/${photo.fileName}`}
                                alt={photo.originalFileName}
                                className="w-full h-32 object-cover rounded-lg border border-gray-200 cursor-pointer hover:shadow-lg transition-shadow"
                                onClick={() => window.open(`/uploads/girlscorner/${photo.fileName}`, '_blank')}
                              />
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="mt-8 flex items-center justify-center gap-2">
              <button
                onClick={() => setPage(page - 1)}
                disabled={page === 1}
                className="px-4 py-2 bg-white border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
              >
                ← Previous
              </button>
              <span className="px-4 py-2 text-gray-600">
                Page {page} of {totalPages}
              </span>
              <button
                onClick={() => setPage(page + 1)}
                disabled={page === totalPages}
                className="px-4 py-2 bg-white border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
              >
                Next →
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

